// Prefs bridge backed by web_prefs (jsonb). Persists per-user
// settings (theme, spellcheck, sidebar widths, etc.) so they sync
// across devices instead of being trapped in one browser's
// localStorage.
//
// The renderer reads prefs via direct `localStorage.getItem(...)`
// calls today (no IPC). Rather than refactor that, this bridge sits
// at a lower level: it hydrates localStorage from web_prefs on sign
// in, and writes web_prefs whenever localStorage changes. Net: the
// renderer's existing prefs code keeps working unchanged, and the
// user gets cross-device sync as a side effect.

// Keys we mirror. Anything else stays browser-local (transient state).
const SYNCED_KEYS = [
  'lastslate.theme',
  'lastslate.spellcheck',
  'lastslate.aiPaneVisible',
  'lastslate.sidebarWidth',
  'lastslate.aiPaneWidth',
  'lastslate.aiQuality',
  'lastslate.zoom',
];

function snapshot() {
  const out = {};
  for (const k of SYNCED_KEYS) {
    const v = localStorage.getItem(k);
    if (v != null) out[k] = v;
  }
  return out;
}

function applyToLocal(snapshotObj) {
  if (!snapshotObj || typeof snapshotObj !== 'object') return;
  for (const k of SYNCED_KEYS) {
    if (snapshotObj[k] != null) localStorage.setItem(k, snapshotObj[k]);
  }
}

export function installPrefsSync({ supabase }) {
  if (!supabase) return;

  let pushTimer = null;
  let lastPushed = '';

  async function pull() {
    const { data: sess } = await supabase.auth.getSession();
    const userId = sess?.session?.user?.id;
    if (!userId) return;
    try {
      const { data } = await supabase
        .from('web_prefs')
        .select('data')
        .eq('user_id', userId)
        .maybeSingle();
      if (data?.data) applyToLocal(data.data);
    } catch {
      /* no-op — prefs sync is best-effort */
    }
  }

  async function push() {
    const { data: sess } = await supabase.auth.getSession();
    const userId = sess?.session?.user?.id;
    if (!userId) return;
    const snap = snapshot();
    const serialized = JSON.stringify(snap);
    if (serialized === lastPushed) return;
    lastPushed = serialized;
    try {
      await supabase
        .from('web_prefs')
        .upsert(
          { user_id: userId, data: snap, updated_at: new Date().toISOString() },
          { onConflict: 'user_id' },
        );
    } catch {
      /* no-op */
    }
  }

  function schedulePush() {
    if (pushTimer) clearTimeout(pushTimer);
    pushTimer = setTimeout(push, 1200);
  }

  // localStorage doesn't fire its own 'storage' event within the same
  // window — only across tabs. Intercept setItem to detect local
  // changes and batch them.
  const origSetItem = localStorage.setItem.bind(localStorage);
  localStorage.setItem = function (key, value) {
    origSetItem(key, value);
    if (SYNCED_KEYS.includes(key)) schedulePush();
  };
  window.addEventListener('storage', (ev) => {
    if (ev.key && SYNCED_KEYS.includes(ev.key)) schedulePush();
  });

  supabase.auth.onAuthStateChange((event) => {
    if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
      pull();
    }
  });
}
